import React, { useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../../../../router/all_routes";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ImageWithBasePath from "../../../../../../core/common/imageWithBasePath";
import { useGetInvoiceByIdQuery } from "../api/invoiceApi";
import { format } from "date-fns";

const StudentFeesInvoice: React.FC = () => {
  const routes = all_routes;
  const { id, invoiceId } = useParams<{ id: string; invoiceId: string }>();
  const navigate = useNavigate();
  const invoiceRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const {
    data: invoiceResponse,
    isLoading,
    error,
  } = useGetInvoiceByIdQuery(invoiceId || "");

  const invoiceData = invoiceResponse?.data?.invoice;

  const studentStatus = invoiceData?.student?.status;

  const styles = {
    gradientBg: {
      background: "linear-gradient(135deg, var(--bs-primary) 0%, #2c80ff 100%)",
    },
    gradientText: {
      color: "var(--bs-primary)",
      fontWeight: "bold",
    },
    letterSpacing1: {
      letterSpacing: "1px",
    },
    letterSpacing2: {
      letterSpacing: "2px",
    },
    dashedBorder: {
      border: "2px dashed rgba(13, 110, 253, 0.3)",
    },
    infoSection: {
      borderLeft: "3px solid var(--bs-primary)",
      paddingLeft: "1rem",
    },
    watermark: {
      position: "absolute" as const,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%) rotate(-45deg)",
      fontSize: "100px",
      opacity: 0.03,
      color: "var(--bs-primary)",
      pointerEvents: "none" as const,
      fontWeight: "bold",
    },
    roundedCorners: {
      borderRadius: "0.5rem",
    },
    logoContainer: {
      background: "rgba(255, 255, 255, 0.15)",
      borderRadius: "0.5rem",
      padding: "0.5rem 1rem",
      display: "inline-block",
    },
    invoiceInfoContainer: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
    },
  };

  const handleBack = () => {
    // navigate(`${routes.studentFees.replace(":id", id || "")}`);
    navigate(-1);
  };

  const handleDownload = async () => {
    if (!invoiceRef.current || isDownloading) return;

    setIsDownloading(true);
    try {
      const invoiceElement = invoiceRef.current;
      const canvas = await html2canvas(invoiceElement, {
        scale: 1,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Calculate vertical centering with top padding
      const topPadding = 20; // 20mm top padding
      const verticalPosition = (pageHeight - imgHeight) / 2 - topPadding;

      // Add image with vertical positioning
      pdf.addImage(
        imgData,
        "PNG",
        0, // x position (left aligned)
        Math.max(verticalPosition, 0), // y position (centered with padding)
        imgWidth,
        imgHeight
      );

      pdf.save(`invoice-${invoiceData?.id || "unknown"}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="card">
                <div className="card-body text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading invoice data...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !invoiceData) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-danger mb-3">
                    <i className="ti ti-alert-circle fa-3x"></i>
                  </div>
                  <h3>Invoice Not Found</h3>
                  <p>The requested invoice could not be loaded.</p>
                  <Link
                    to={routes.studentFees.replace(":id", id || "")}
                    className="btn btn-outline-primary mt-3"
                  >
                    <i className="ti ti-arrow-left me-2"></i>
                    Back to Invoices
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Format dates from API
  const invoiceDate = invoiceData.invoice_date
    ? new Date(invoiceData.invoice_date)
    : new Date();
  const dueDate = invoiceData.due_date
    ? new Date(invoiceData.due_date)
    : new Date();

  const formattedInvoiceDate = format(invoiceDate, "yyyy-MM-dd");
  const formattedDueDate = format(dueDate, "yyyy-MM-dd");

  // Prepare invoice item
  const invoiceItem = {
    id: 1,
    description:
      invoiceData.type === "registrationfee"
        ? `Registration Fee - ${invoiceData.course.name}`
        : `${invoiceData.course.name} ${invoiceData.stage?.name || ""} Fee`,
    amount: parseFloat(invoiceData.amount),
  };

  // Calculate total
  const subtotal = parseFloat(invoiceData.amount);

  // Fix discount calculation for percentage type
  let discount = 0;
  if (invoiceData.discount_value) {
    if (invoiceData.discount_type === "percent") {
      // Calculate percentage-based discount correctly
      const percentValue = parseFloat(invoiceData.discount_value);
      discount = (subtotal * percentValue) / 100;
    } else {
      // Fixed amount discount
      discount = parseFloat(invoiceData.discount_value);
    }
  }

  const total = parseFloat(invoiceData.payable);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Invoice Details</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.studentFees.replace(":id", id || "")}>
                    Student Fees
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Invoice {invoiceData.invoice_number}
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="mb-2 me-2">
              <button
                onClick={handleBack}
                className="btn btn-outline-primary d-flex align-items-center"
              >
                <i className="ti ti-arrow-left me-2" />
                Back
              </button>
            </div>
            {studentStatus === 'active' && invoiceData.invoice_payment_status === 'pending' && (
              <div className="mb-2 me-2">
                <Link
                  to={`${routes.studentFeesDiscount
                    .replace(":id", id || "")
                    .replace(":invoiceId", invoiceId || "")}`}
                  className="btn btn-outline-success d-flex align-items-center"
                >
                  <i className="ti ti-discount-2 me-2" />
                  Apply Discount
                </Link>
              </div>
            )}
            <div className="mb-2">
              <button
                onClick={handleDownload}
                className="btn btn-primary d-flex align-items-center"
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="ti ti-download me-2" />
                    Download PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div
              className="card shadow-sm border-0 overflow-hidden"
              ref={invoiceRef}
            >
              {/* Watermark */}
              <div className="position-relative">
                <div style={styles.watermark}>INVOICE</div>
              </div>

              {/* Header Section - Single Row */}
              <div
                className="card-header text-white p-4"
                style={styles.gradientBg}
              >
                <div className="d-flex justify-content-between align-items-center">
                  {/* Logo Section */}
                  <div style={styles.logoContainer}>
                    <ImageWithBasePath
                      src="assets/img/logo.png"
                      alt="School Logo"
                      className="img-fluid"
                      width={120}
                      height={30}
                    />
                  </div>

                  {/* Empty div to maintain layout */}
                  <div></div>

                  {/* Invoice Title moved to right with matching logo styling */}
                  <div style={styles.logoContainer} className="text-end">
                    <h3 className="mb-0 fw-bold" style={styles.letterSpacing2}>
                      INVOICE
                    </h3>
                    <div className="text-white opacity-75">
                      {/* INV-{invoiceData.id} */}
                      {invoiceData.invoice_number}
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-body p-4 position-relative">
                {/* Date Information - in a single row */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="d-flex justify-content-between">
                      <div className="d-flex align-items-center">
                        <i className="ti ti-calendar text-primary me-2"></i>
                        <span className="fw-semibold">Invoice Date:</span>
                        <span className="ms-2">{formattedInvoiceDate}</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <i className="ti ti-calendar-due text-primary me-2"></i>
                        <span className="fw-semibold">Due Date:</span>
                        <span className="ms-2">{formattedDueDate}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div style={styles.infoSection} className="mb-3">
                      <h5 className="fw-bold text-primary mb-3">From</h5>
                      <div className="fw-semibold">
                        {invoiceData.center.name}
                      </div>
                      <div className="text-muted">
                        {invoiceData.center.address}, {invoiceData.center.zone},{" "}
                        {invoiceData.center.district}
                      </div>
                      <div className="text-muted">
                        <i className="ti ti-phone text-primary me-1"></i>{" "}
                        {invoiceData.center.phone}
                      </div>
                      <div className="text-muted">
                        <i className="ti ti-mail text-primary me-1"></i>{" "}
                        {invoiceData.center.email}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div
                      style={{
                        ...styles.infoSection,
                        borderLeft: "3px solid var(--bs-primary)",
                        borderRight: "none",
                        paddingRight: "0",
                        paddingLeft: "1rem",
                        textAlign: "left",
                      }}
                      className="mb-3"
                    >
                      <h5 className="fw-bold text-primary mb-3">To</h5>
                      <div className="fw-semibold">
                        {invoiceData.student.name}
                      </div>
                      <div className="text-muted">
                        {invoiceData.student.address?.address}
                      </div>
                      <div className="text-muted">
                        {invoiceData.student.address?.state},{" "}
                        {invoiceData.student.address?.district}
                        <div className="text-muted">
                          {invoiceData.student.address?.country} -{" "}
                          {invoiceData.student.address?.pincode}
                        </div>
                      </div>
                      <div className="text-muted">
                        <i className="ti ti-phone text-primary me-1"></i>{" "}
                        {invoiceData.student.profile?.phone || "N/A"}
                      </div>
                      <div className="text-muted">
                        <i className="ti ti-mail text-primary me-1"></i>{" "}
                        {invoiceData.student.profile?.email || "N/A"}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Invoice Items */}
                <div className="table-responsive mb-4">
                  <table
                    className="table table-bordered"
                    style={styles.roundedCorners}
                  >
                    <thead className="bg-light">
                      <tr>
                        <th>Description</th>
                        <th>Fee Type</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr key={invoiceItem.id}>
                        <td>
                          {invoiceData.type === "registrationfee"
                            ? `Registration Fee - ${invoiceData.course.name}`
                            : `${invoiceData.course.name} ${invoiceData.stage?.name || ""
                            } Fee`}
                        </td>
                        <td>
                          {invoiceData.type === "registrationfee"
                            ? "Registration Fee"
                            : "Course Fee"}
                        </td>
                        <td className="text-end">
                          ₹{invoiceItem.amount.toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Total Calculation */}
                <div className="row">
                  <div className="col-md-6">
                    <div
                      className="bg-light p-3 rounded"
                      style={styles.dashedBorder}
                    >
                      <div className="d-flex align-items-center mb-2">
                        <span className="avatar avatar-sm bg-primary text-white rounded-circle me-2">
                          <i className="ti ti-receipt"></i>
                        </span>
                        <h6 className="fw-bold mb-0">Payment Instructions</h6>
                      </div>
                      <div className="mt-3">
                        <div className="d-flex align-items-center mb-2">
                          <i className="ti ti-info-circle text-primary me-2"></i>
                          <span
                            className="fw-semibold"
                            style={styles.gradientText}
                          >
                            Note:
                          </span>
                          <span className="ms-2" style={{ fontSize: "12px" }}>
                            Please make payment before the due date
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div
                      className="bg-light p-3 rounded"
                      style={{
                        ...styles.dashedBorder,
                        borderColor: "rgba(13, 110, 253, 0.5)",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Subtotal:</span>
                        <span>₹{subtotal.toFixed(2)}</span>
                      </div>
                      {discount > 0 && (
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span>
                            Discount
                            {invoiceData.discount_type === "percent"
                              ? ` (${invoiceData.discount_value}%)`
                              : ""}
                            :
                          </span>
                          <span className="text-success">
                            -₹{discount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="d-flex justify-content-between align-items-center fw-bold pt-2 border-top">
                        <span className="text-primary">TOTAL:</span>
                        <span className="text-primary">
                          ₹{total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes and Terms */}
                <div className="mt-4 text-center">
                  <div className="text-muted">
                    <p className="mb-0">
                      This is a computer-generated invoice, no signature
                      required.
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div
                className="card-footer text-white text-center py-3"
                style={styles.gradientBg}
              >
                <p className="mb-0" style={styles.letterSpacing1}>
                  For any questions regarding this invoice, please contact{" "}
                  {invoiceData.center.email}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentFeesInvoice;
